import React, { useState } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Input,
  Select,
  HStack,
  Spinner,
  Center,
  Text,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { useAdminUsers, useDeactivateUser, useReactivateUser, useVerifyUser, User } from "api/admin";

export default function Users(): JSX.Element {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [deactivated, setDeactivated] = useState("");
  
  const { data, isLoading, isError } = useAdminUsers({ page, limit: 10, search, role, deactivated });
  const deactivateMut = useDeactivateUser();
  const reactivateMut = useReactivateUser();
  const verifyMut = useVerifyUser();
  const toast = useToast();

  const handleAction = async (action: any, id: string, name: string) => {
    try {
      await action.mutateAsync(id);
      toast({ title: `Successfully updated ${name}`, status: "success", isClosable: true });
    } catch (e) {
      toast({ title: "Action failed", status: "error", isClosable: true });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="20px" mb="20px">
        <HStack mb="20px" spacing={4}>
          <Input placeholder="Search name/email" value={search} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setSearch(e.target.value)} />
          <Select placeholder="All Roles" value={role} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setRole(e.target.value)}>
            <option value="user">User</option>
            <option value="vendor">Vendor</option>
            <option value="admin">Admin</option>
          </Select>
          <Select placeholder="All Status" value={deactivated} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setDeactivated(e.target.value)}>
            <option value="false">Active</option>
            <option value="true">Deactivated</option>
          </Select>
        </HStack>

        {isLoading ? (
          <Center p="40px"><Spinner /></Center>
        ) : isError ? (
          <Center p="40px"><Text color="red.500">Failed to load data.</Text></Center>
        ) : !data?.data?.length ? (
          <Center p="40px"><Text>No data found.</Text></Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.data?.map((u: User) => (
                  <Tr key={u.id}>
                    <Td>{u.fullName}</Td>
                    <Td>{u.email}</Td>
                    <Td>{u.role}</Td>
                    <Td>
                      <Badge colorScheme={u.deactivated ? "red" : "green"}>
                        {u.deactivated ? "Deactivated" : "Active"}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {u.deactivated ? (
                          <Button size="sm" colorScheme="green" onClick={() => handleAction(reactivateMut, u.id, u.fullName)}>Reactivate</Button>
                        ) : (
                          <Button size="sm" colorScheme="red" onClick={() => handleAction(deactivateMut, u.id, u.fullName)}>Deactivate</Button>
                        )}
                        {!u.deactivated && u.role === 'vendor' && (
                           <Button size="sm" onClick={() => handleAction(verifyMut, u.id, u.fullName)}>Verify</Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Flex justify="space-between" mt="4">
              <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Text>Page {data?.page} of {(data?.totalPage || 0)}</Text>
              <Button disabled={page >= (data?.totalPage || 0)} onClick={() => setPage(p => p + 1)}>Next</Button>
            </Flex>
          </Box>
        )}
      </Card>
    </Box>
  );
}
